import { useState, useEffect, useRef } from 'react';
import { Plus, Check, Paperclip, X, FileText, FileImage, FilePdf, FileDoc, FileXls, FilePpt, FileZip, FileCsv } from '@phosphor-icons/react';
import { Droppable } from '@hello-pangea/dnd';
import TicketCard from './TicketCard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useT } from '../i18n/I18nContext';
import api from '../lib/api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const absolutizeUrl = (src) => (src?.startsWith('/api/') ? `${BACKEND_URL}${src}` : src);

const ATTACH_ACCEPT = [
  'image/png', 'image/jpeg', 'image/gif', 'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain', 'text/csv', 'text/markdown',
  'application/zip', 'application/json',
].join(',');

function serializeToMarkdown(el) {
  let text = '';
  for (const node of el.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent;
    } else if (node.nodeName === 'IMG') {
      const dataSrc = node.getAttribute('data-src');
      if (dataSrc) text += `![](${dataSrc})`;
    } else if (node.nodeName === 'BR') {
      text += '\n';
    } else if (node.nodeName === 'DIV' || node.nodeName === 'P') {
      if (text && !text.endsWith('\n')) text += '\n';
      text += serializeToMarkdown(node);
    } else {
      text += serializeToMarkdown(node);
    }
  }
  return text;
}

const STATUS_KEYS = {
  todo: 'kanban.backlog',
  in_progress: 'kanban.inProgress',
  blocked: 'kanban.blocked',
  testing: 'kanban.testing',
  review: 'kanban.review',
  done: 'kanban.done',
};

const STATUS_DOTS = {
  todo: 'bg-zinc-400',
  in_progress: 'bg-purple-500',
  blocked: 'bg-red-500',
  testing: 'bg-sky-500',
  review: 'bg-amber-500',
  done: 'bg-emerald-500',
};

const initialForm = () => ({
  title: '',
  description: '',
  priority: 'medium',
  assignee_id: '',
  labels: '',
  target_repo_ids: null, // null = "all repos"
  attachments: [],
});

function iconForMime(mime) {
  if (!mime) return FileText;
  if (mime.startsWith('image/')) return FileImage;
  if (mime === 'application/pdf') return FilePdf;
  if (mime.includes('word')) return FileDoc;
  if (mime.includes('sheet') || mime === 'application/vnd.ms-excel') return FileXls;
  if (mime.includes('presentation') || mime === 'application/vnd.ms-powerpoint') return FilePpt;
  if (mime === 'application/zip') return FileZip;
  if (mime === 'text/csv') return FileCsv;
  return FileText;
}

function formatBytes(n) {
  if (!Number.isFinite(n) || n <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

export default function KanbanColumn({ status, tickets, users, projectMembers, projectId, project, onCreate, onTicketClick, onTicketDelete, onTicketAssigneeChange, landedTicketId, countBumpKey }) {
  // ``users`` is the global list (kept for name/initial lookups so removed
  // members still render on cards); ``projectMembers`` is what populates the
  // assignee dropdown so you can't pick someone outside the project.
  const assignableUsers = projectMembers ?? users ?? [];
  const t = useT();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [creating, setCreating] = useState(false);
  const [popping, setPopping] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const descRef = useRef(null);
  const fileInputRef = useRef(null);

  const syncDesc = () => {
    if (descRef.current) setForm((f) => ({ ...f, description: serializeToMarkdown(descRef.current) }));
  };

  const handleDescPaste = async (e) => {
    const cd = e.clipboardData;
    if (!cd) return;

    // Prevent the browser from inserting clipboard HTML/images itself.
    e.preventDefault();

    // Capture selection synchronously — it's gone after the first await.
    const sel = window.getSelection();
    const range = sel?.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;

    let imageFile = Array.from(cd.files || []).find((f) => f.type.startsWith('image/'));
    if (!imageFile) {
      const fileItem = Array.from(cd.items || []).find(
        (it) => it.kind === 'file' && it.type.startsWith('image/'),
      );
      if (fileItem) imageFile = fileItem.getAsFile();
    }

    let htmlDataUrl = null;
    if (!imageFile) {
      const html = cd.getData('text/html');
      const m = html && html.match(/<img[^>]+src="(data:image\/[a-zA-Z+]+;base64,[^"]+)"/);
      if (m) htmlDataUrl = m[1];
    }

    if (!imageFile && !htmlDataUrl) {
      const text = cd.getData('text/plain');
      if (text) document.execCommand('insertText', false, text);
      return;
    }

    // Insert "Загружается..." at cursor position.
    const loadingEl = document.createElement('span');
    loadingEl.textContent = 'Загружается...';
    loadingEl.style.color = '#94a3b8';
    loadingEl.style.fontStyle = 'italic';
    if (range) {
      range.deleteContents();
      range.insertNode(loadingEl);
      const after = document.createRange();
      after.setStartAfter(loadingEl);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);
    } else {
      descRef.current?.appendChild(loadingEl);
    }

    if (!imageFile && htmlDataUrl) {
      const res = await fetch(htmlDataUrl);
      const blob = await res.blob();
      imageFile = new File([blob], 'pasted.png', { type: blob.type });
    }

    try {
      const fd = new FormData();
      fd.append('file', imageFile, imageFile.name || 'pasted.png');
      const { data } = await api.post('/attachments', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const img = document.createElement('img');
      img.src = absolutizeUrl(data.url);
      img.setAttribute('data-src', data.url);
      img.style.cssText = 'max-height:200px;max-width:100%;border-radius:4px;display:block;margin:4px 0';
      loadingEl.replaceWith(img);
      // Move cursor after the inserted image.
      const newSel = window.getSelection();
      const newRange = document.createRange();
      newRange.setStartAfter(img);
      newRange.collapse(true);
      newSel.removeAllRanges();
      newSel.addRange(newRange);
    } catch (err) {
      loadingEl.remove();
      toast.error(err?.response?.data?.detail || t('ticketDetail.uploadFailedToast'));
    }
    syncDesc();
  };

  const handleAttachClick = () => {
    if (uploadingAttachment) return;
    fileInputRef.current?.click();
  };

  const handleAttachChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingAttachment(true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const { data } = await api.post('/attachments', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({
        ...f,
        attachments: [...(f.attachments || []), {
          filename: data.filename,
          original_name: data.original_name || file.name,
          url: data.url,
          mime: data.mime || file.type,
          bytes: data.bytes || file.size,
          is_image: data.is_image ?? file.type.startsWith('image/'),
        }],
      }));
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 415) toast.error(detail || t('newTicket.attachUnsupported'));
      else if (status === 413) toast.error(detail || t('newTicket.attachTooLarge'));
      else toast.error(detail || t('ticketDetail.uploadFailedToast'));
    } finally {
      setUploadingAttachment(false);
    }
  };

  const removeAttachment = (filename) => {
    setForm((f) => ({
      ...f,
      attachments: (f.attachments || []).filter((a) => a.filename !== filename),
    }));
  };

  useEffect(() => {
    if (!countBumpKey) return;
    setPopping(false);
    const raf = requestAnimationFrame(() => setPopping(true));
    const popTimer = setTimeout(() => setPopping(false), 600);
    return () => { cancelAnimationFrame(raf); clearTimeout(popTimer); };
  }, [countBumpKey]);
  const repos = project?.repos || [];
  const multiRepo = repos.length > 1;

  const toggleRepo = (rid) => {
    setForm((f) => {
      const current = f.target_repo_ids ?? repos.map((r) => r.id);
      const next = current.includes(rid) ? current.filter((x) => x !== rid) : [...current, rid];
      // If user toggles back to the full set, store ``null`` so we send no override.
      const allIds = repos.map((r) => r.id);
      const isAll = next.length === allIds.length && allIds.every((x) => next.includes(x));
      return { ...f, target_repo_ids: isAll ? null : next };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        title: form.title,
        description: descRef.current ? serializeToMarkdown(descRef.current) : form.description,
        priority: form.priority,
        assignee_id: form.assignee_id || null,
        labels: form.labels.split(',').map((l) => l.trim()).filter(Boolean),
        attachments: form.attachments || [],
      };
      // Only send target_repo_ids when the user explicitly narrowed the set.
      if (form.target_repo_ids && form.target_repo_ids.length > 0) {
        payload.target_repo_ids = form.target_repo_ids;
      }
      await onCreate(payload);
      setForm(initialForm());
      if (descRef.current) descRef.current.innerHTML = '';
      setOpen(false);
    } finally {
      setCreating(false);
    }
  };

  const selectedRepoIds = form.target_repo_ids ?? repos.map((r) => r.id);

  return (
    <div className="flex-1 min-w-[200px] flex flex-col gap-3 min-h-0 h-full max-h-full" data-testid={`column-${status}`}>
      <div className="flex h-5 items-center justify-between px-1 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className={`w-1.5 h-1.5 rounded-full ${STATUS_DOTS[status]}`} />
          <h3 className="text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-700">
            {t(STATUS_KEYS[status])}
          </h3>
          <span className={`text-[10px] font-mono text-zinc-400 ${popping ? 'count-pop' : ''}`}>{tickets.length}</span>
        </div>
        {status === 'todo' && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(initialForm()); if (descRef.current) descRef.current.innerHTML = ''; } }}>
            <DialogTrigger asChild>
              <button data-testid={`add-ticket-${status}-btn`} className="w-5 h-5 hover:bg-zinc-200 rounded-sm flex items-center justify-center">
                <Plus size={14} />
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display font-black tracking-tight">{t('newTicket.title')}</DialogTitle>
                <DialogDescription className="text-xs text-zinc-500">{t('newTicket.description')}</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">{t('newTicket.titleLabel')}</Label>
                  <Input data-testid="ticket-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm mt-1" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-wider font-semibold">{t('newTicket.descriptionLabel')}</Label>
                    <button
                      type="button"
                      data-testid="ticket-attach-btn"
                      onClick={handleAttachClick}
                      disabled={uploadingAttachment}
                      aria-label={t('newTicket.attachAria')}
                      title={t('newTicket.attachBtn')}
                      className="inline-flex items-center gap-1 text-[11px] font-mono text-zinc-500 hover:text-zinc-900 disabled:opacity-50"
                    >
                      <Paperclip size={12} weight="bold" />
                      {uploadingAttachment ? '...' : t('newTicket.attachBtn')}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ATTACH_ACCEPT}
                      data-testid="ticket-attach-input"
                      onChange={handleAttachChange}
                      className="hidden"
                    />
                  </div>
                  <div className="relative mt-1">
                    <div
                      ref={descRef}
                      contentEditable
                      suppressContentEditableWarning
                      data-testid="ticket-description-input"
                      onInput={syncDesc}
                      onPaste={handleDescPaste}
                      className="min-h-[6rem] max-h-[40vh] overflow-y-auto w-full rounded-sm border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    />
                    {!form.description && (
                      <span className="absolute top-2 left-3 text-zinc-400 text-[13px] pointer-events-none select-none">
                        {t('newTicket.descriptionPlaceholder')}
                      </span>
                    )}
                  </div>
                  {(form.attachments || []).length > 0 && (
                    <ul data-testid="ticket-attachments-list" className="mt-2 space-y-1">
                      {form.attachments.map((a) => {
                        const Icon = iconForMime(a.mime);
                        return (
                          <li
                            key={a.filename}
                            data-testid={`ticket-attachment-${a.filename}`}
                            className="flex items-center gap-2 rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-xs"
                          >
                            <Icon size={14} className="text-zinc-500 shrink-0" weight="bold" />
                            <a
                              href={absolutizeUrl(a.url)}
                              target="_blank"
                              rel="noreferrer"
                              className="font-mono text-zinc-800 hover:text-[#0055ff] truncate flex-1"
                              title={a.original_name}
                            >
                              {a.original_name}
                            </a>
                            {a.bytes ? (
                              <span className="font-mono text-[10px] text-zinc-400 shrink-0">{formatBytes(a.bytes)}</span>
                            ) : null}
                            <button
                              type="button"
                              data-testid={`ticket-attachment-remove-${a.filename}`}
                              onClick={() => removeAttachment(a.filename)}
                              aria-label={t('newTicket.attachRemoveAria')}
                              className="text-zinc-400 hover:text-red-600 shrink-0"
                            >
                              <X size={12} weight="bold" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">{t('newTicket.priorityLabel')}</Label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger data-testid="ticket-priority-select" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">{t('priority.low')}</SelectItem>
                        <SelectItem value="medium">{t('priority.medium')}</SelectItem>
                        <SelectItem value="high">{t('priority.high')}</SelectItem>
                        <SelectItem value="urgent">{t('priority.urgent')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">{t('newTicket.assigneeLabel')}</Label>
                    <Select value={form.assignee_id || 'none'} onValueChange={(v) => setForm({ ...form, assignee_id: v === 'none' ? '' : v })}>
                      <SelectTrigger data-testid="ticket-assignee-select" className="rounded-sm mt-1"><SelectValue placeholder={t('newTicket.unassigned')} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('newTicket.unassigned')}</SelectItem>
                        {assignableUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">{t('newTicket.labelsLabel')}</Label>
                  <Input data-testid="ticket-labels-input" placeholder={t('newTicket.labelsPlaceholder')} value={form.labels} onChange={(e) => setForm({ ...form, labels: e.target.value })} className="rounded-sm mt-1 font-mono text-xs" />
                </div>
                {multiRepo && (
                  <div data-testid="ticket-target-repos">
                    <Label className="text-xs uppercase tracking-wider font-semibold">
                      {t('newTicket.targetReposLabel')}
                    </Label>
                    <p className="text-[11px] text-zinc-500 mt-0.5 mb-1.5">{t('newTicket.targetReposHint')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {repos.map((r) => {
                        const active = selectedRepoIds.includes(r.id);
                        return (
                          <button
                            key={r.id}
                            type="button"
                            data-testid={`ticket-target-repo-${r.id}`}
                            onClick={() => toggleRepo(r.id)}
                            className={`inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-sm border transition-colors ${
                              active
                                ? 'bg-zinc-900 text-white border-zinc-900'
                                : 'bg-white text-zinc-400 border-dashed border-zinc-300 hover:border-zinc-500 hover:text-zinc-600'
                            }`}
                            aria-pressed={active}
                          >
                            {active && <Check size={10} weight="bold" />}
                            {r.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button data-testid="ticket-create-submit-btn" type="submit" disabled={creating} className="rounded-sm bg-black text-white hover:bg-zinc-800">
                    {creating ? t('newTicket.submitting') : t('newTicket.submit')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Droppable droppableId={status}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            data-testid={`droppable-${status}`}
            className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-1.5 p-2 rounded-sm transition-colors ${
              snapshot.isDraggingOver
                ? 'bg-blue-50/60 border border-blue-200 border-dashed'
                : 'bg-zinc-100/60 border border-transparent'
            }`}
          >
            {tickets.map((t, i) => (
              <TicketCard
                key={t.id}
                ticket={t}
                index={i}
                users={users}
                projectMembers={assignableUsers}
                onClick={() => onTicketClick(t)}
                onDelete={onTicketDelete}
                onAssigneeChange={onTicketAssigneeChange}
                justLanded={landedTicketId === t.id}
              />
            ))}
            {provided.placeholder}
            {tickets.length === 0 && (
              <div className="text-center text-[11px] text-zinc-400 py-6 font-mono uppercase tracking-wider">{t('kanban.empty')}</div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
}
